<template>
  <BaseModal ref="modalRef" class="max-w-[500px]!" @close="resetForm">
    <form action="" class="bounce-in" @submit.prevent="updatePassword()">
      <div class="">
        <div class="mb-8 flex flex-col gap-y-2 items-center">
          <h2 class="text-2xl text-center font-semibold text-white">Set your new password</h2>
        </div>
        <div class="grid grid-cols-1 gap-y-5">
          <BasePasswordInput v-model="oldPassword" label="Enter old password">
            <template #errors>
              <template v-if="$v.oldPassword.$dirty">
                <span v-for="error in $v.oldPassword.$errors" :key="error.$uid" class="input-error">
                  {{ error.$message }}
                </span>
              </template>
            </template>
          </BasePasswordInput>
          <BasePasswordInput v-model="newPassword" label="Enter new password">
            <template #errors>
              <template v-if="$v.newPassword.$dirty">
                <span v-for="error in $v.newPassword.$errors" :key="error.$uid" class="input-error">
                  {{ error.$message }}
                </span>
              </template>
            </template>
          </BasePasswordInput>
          <p v-if="errorMessage" class="text-cw-red">{{ errorMessage }}</p>
        </div>
        <div class="w-max ml-auto mt-4">
          <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="px-10 py-3.5 ml-auto mt-8" :disabled="isLoading">
            <Loader v-if="isLoading" />
            <p class="!text-base xl:!text-xl">Set password</p>
          </BaseButton>
        </div>
      </div>
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import { useTemplateRef, ref } from 'vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BasePasswordInput from '@/components/base/BasePasswordInput.vue'
import Loader from '@/components/interface/Loader.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import UserService from '@/services/api/user-service'
import { useHelpers } from '@/composables/useHelpers'

const modalRef = useTemplateRef('modalRef')
const oldPassword = ref('')
const newPassword = ref('')
const errorMessage = ref('')
const isLoading = ref(false)
const { sleep, showToast } = useHelpers()

const $v = useVuelidate(
  {
    oldPassword: {
      required: helpers.withMessage('Enter your old password', required),
      $autoDirty: true
    },
    newPassword: {
      required: helpers.withMessage('Enter your new password', required),
      $autoDirty: true
    }
  },
  { newPassword, oldPassword }
)

const open = () => {
  modalRef.value?.open()
}

const close = () => {
  modalRef.value?.close()
}

const resetForm = async () => {
  await sleep(200)
  newPassword.value = ''
  oldPassword.value = ''
  errorMessage.value = ''
  $v.value.$reset()
}

const updatePassword = async () => {
  try {
    isLoading.value = true
    await UserService.updatePassword({
      oldPassword: oldPassword.value,
      newPassword: newPassword.value
    })
    close()
    showToast('You have successfully updated your password.')
  } catch (error: any) {
    errorMessage.value = error?.data?.message || 'An error occurred. Please try again.'
  } finally {
    isLoading.value = false
  }
}

defineExpose({
  open,
  close
})
</script>
